import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import { pushApi } from "../api/client";

/**
 * 이 기기로 알림을 받을지.
 *
 * **기기마다 따로다.** 계정 설정이 아니라 브라우저가 들고 있는 구독이라, 폰에서
 * 켜도 노트북은 그대로다. 그게 맞다 — 알림은 "지금 내 손에 있는 것"이 울려야
 * 하고, 노트북을 두고 나온 사람에게 노트북 알림은 아무 소용이 없다.
 *
 * 서비스 워커(`public/sw.js`)가 실제로 알림을 띄우고, 서버는 그 워커에게
 * 보낸다(`apps/api/src/events/push.ts`). 여기는 그 둘을 잇는 등록 절차만 한다.
 */

export type PushState =
  /** 이 브라우저가 푸시를 못 한다. 또는 서버에 키가 없다 */
  | "unsupported"
  /** 쓸 수 있는데 아직 안 켰다 */
  | "off"
  /** 이 기기로 알림이 온다 */
  | "on"
  /** 사용자가 브라우저 설정에서 막았다. 앱에서는 되돌릴 수 없다 */
  | "denied"
  /** 켜거나 끄는 중 */
  | "busy";

/**
 * base64url(VAPID 공개키) → 바이트. 구독 API가 문자열이 아니라 바이트를 받는다.
 *
 * `ArrayBuffer`로 돌려주는 이유는 타입 때문이다 — `Uint8Array`의 buffer는
 * `SharedArrayBuffer`일 수도 있어서 `BufferSource`로 바로 안 들어간다.
 */
function decodeKey(base64Url: string): ArrayBuffer {
  const padded = base64Url.padEnd(
    base64Url.length + ((4 - (base64Url.length % 4)) % 4),
    "=",
  );
  const binary = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}

/** 브라우저가 준 구독을 서버가 받는 모양으로. `toJSON`은 표준이라 믿어도 된다 */
function toInput(subscription: PushSubscription) {
  const json = subscription.toJSON();
  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: json.keys?.p256dh ?? "",
      auth: json.keys?.auth ?? "",
    },
  };
}

const supported =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export function usePushNotifications(enabled: boolean) {
  /**
   * 서버가 알림을 보낼 수 있는가.
   *
   * 키가 없으면(개발 중이거나 아직 안 넣었으면) 화면에서 이 줄을 통째로 지운다.
   * 눌러도 아무 일이 없는 단추를 두면 사용자가 자기 브라우저를 의심한다.
   */
  const keyQuery = useQuery({
    queryKey: ["push-key"],
    queryFn: pushApi.key,
    enabled: enabled && supported,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  const [state, setState] = useState<PushState>("off");

  /**
   * 지금 상태를 브라우저에서 다시 읽는다.
   *
   * 앱이 들고 있는 값이 아니라 **브라우저가 사실상 어떤 상태인지**가 정본이다.
   * 사용자는 브라우저 설정에서 언제든 알림을 껐다 켤 수 있고, 그러면 우리가
   * 기억해둔 값은 조용히 거짓이 된다.
   */
  const refresh = useCallback(async () => {
    if (!supported) {
      setState("unsupported");
      return;
    }
    if (Notification.permission === "denied") {
      setState("denied");
      return;
    }
    if (Notification.permission !== "granted") {
      setState("off");
      return;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    setState(subscription ? "on" : "off");

    /*
     * 허락은 받았는데 서버가 이 기기를 모를 수 있다 — 서버 DB를 옮겼거나,
     * 브라우저가 구독을 스스로 갱신했을 때다. 그때 조용히 다시 등록한다.
     * 사용자 눈에는 "켜 뒀는데 안 온다"로만 보이므로 물어볼 수도 없다.
     */
    if (subscription) {
      await pushApi.subscribe(toInput(subscription)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
  }, [enabled, refresh]);

  const turnOn = useCallback(async () => {
    const publicKey = keyQuery.data?.publicKey;
    if (!supported || !publicKey) return;

    setState("busy");
    try {
      /*
       * 허락을 먼저 받는다. **누른 직후여야 한다** — 브라우저는 사용자가 방금
       * 무엇을 누른 흐름에서만 이 창을 띄워준다.
       */
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState(permission === "denied" ? "denied" : "off");
        return;
      }

      // 등록은 여러 번 불러도 안전하다. 이미 있으면 그것을 돌려준다.
      const registration = await navigator.serviceWorker.register("/sw.js");
      // 방금 등록한 워커는 아직 안 깨어 있을 수 있다. 준비될 때까지 기다린다.
      await navigator.serviceWorker.ready;

      const subscription =
        (await registration.pushManager.getSubscription()) ??
        (await registration.pushManager.subscribe({
          // 알림 없는 조용한 푸시는 브라우저가 거절한다. 우리도 쓸 일이 없다.
          userVisibleOnly: true,
          applicationServerKey: decodeKey(publicKey),
        }));

      await pushApi.subscribe(toInput(subscription));
      setState("on");
    } catch {
      // 실패하면 안 켠 것으로 되돌린다. "켜졌다"고 해두면 안 오는 이유를
      // 영영 알 수 없다.
      setState(Notification.permission === "denied" ? "denied" : "off");
    }
  }, [keyQuery.data]);

  const turnOff = useCallback(async () => {
    if (!supported) return;

    setState("busy");
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();

      if (subscription) {
        // 서버에서 먼저 지운다. 브라우저 쪽만 끊고 실패하면 서버는 죽은
        // endpoint로 계속 쏘게 된다.
        await pushApi.unsubscribe(subscription.endpoint).catch(() => {});
        await subscription.unsubscribe();
      }
      setState("off");
    } catch {
      void refresh();
    }
  }, [refresh]);

  return {
    /** 서버에 키가 없으면 이 기능 자체를 화면에서 지운다 */
    available: supported && keyQuery.data?.enabled === true,
    state,
    turnOn,
    turnOff,
  };
}
